import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createBrowserRouter } from 'react-router'
import '@fontsource/ubuntu/300.css'
import '@fontsource/ubuntu/400.css'
import '@fontsource/ubuntu/500.css'
import '@fontsource/ubuntu/700.css'
import '@fontsource/oswald/500.css'
import '@fontsource/oswald/600.css'
import '@fontsource/oswald/700.css'
import '@fontsource/caveat/600.css'
import './styles/app.css'
import { ToastProvider } from './components/ui/Toast.jsx'
import { AppShell } from './layout/AppShell.jsx'
import { SessionProvider } from './lib/session.jsx'
import HomePage from './pages/HomePage.jsx'
import { NotFoundPage, RouteErrorPage } from './pages/PlaceholderPages.jsx'
import SignInPage from './pages/SignInPage.jsx'

// Pages beyond the home and sign-in screens load on demand. If a page's file
// is gone because the site was updated since this tab opened, the error page
// reloads to get the new build.
const page = (load, name) => () => load().then((module) => ({ Component: module[name] }))
const legal = () => import('./pages/LegalPages.jsx')
const support = () => import('./pages/support/SupportPages.jsx')
const accounts = () => import('./pages/players/AccountPages.jsx')
const players = () => import('./pages/players/PlayerPages.jsx')
const highlights = () => import('./pages/highlights/HighlightsPages.jsx')
const playerStats = () => import('./pages/stats/PredggPages.jsx')
const teams = () => import('./pages/teams/TeamListPages.jsx')
const teamPage = () => import('./pages/teams/TeamPage.jsx')
const manage = () => import('./pages/teams/ManageTeamPage.jsx')
const orgs = () => import('./pages/orgs/OrgPages.jsx')
const eventPage = () => import('./pages/events/EventPage.jsx')
const eventDashboard = () => import('./pages/events/manage/ManageEventPage.jsx')

const router = createBrowserRouter([
  // Full-screen and chromeless, for casters and stream overlays.
  { path: 'e/:slug/matches/:id/spectate', lazy: page(() => import('./pages/events/DraftRoom.jsx'), 'DraftSpectatePage'), errorElement: <RouteErrorPage /> },
  { path: 'e/:slug/overlay/:kind', lazy: page(() => import('./pages/events/Overlays.jsx'), 'OverlayPage'), errorElement: <RouteErrorPage /> },
  { path: 'e/:slug/draft-day/stream', lazy: page(() => import('./pages/events/PlayerDraft.jsx'), 'DraftStreamPage'), errorElement: <RouteErrorPage /> },
  {
    element: <AppShell />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        // Errors show inside the site's frame, with the nav still there.
        errorElement: <RouteErrorPage />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'signin', element: <SignInPage /> },
          { path: 'styleguide', lazy: page(() => import('./pages/StyleguidePage.jsx'), 'default') },
          { path: 'welcome', lazy: page(accounts, 'WelcomePage') },
          { path: 'settings', lazy: page(accounts, 'ProfileSettingsPage') },
          { path: 'settings/profile', lazy: page(accounts, 'ProfileSettingsPage') },
          { path: 'notifications', lazy: page(() => import('./pages/NotificationsPage.jsx'), 'NotificationsPage') },
          { path: 'players', lazy: page(players, 'PlayersPage') },
          { path: 'free-agents', lazy: page(() => import('./pages/players/FreeAgentsPage.jsx'), 'FreeAgentsPage') },
          { path: 'scrims', lazy: page(() => import('./pages/scrims/ScrimsPage.jsx'), 'ScrimsPage') },
          { path: 'scrims/:id', lazy: page(() => import('./pages/scrims/ScrimPage.jsx'), 'ScrimPage') },
          { path: 'scrims/:id/draft/:game', lazy: page(() => import('./pages/events/DraftRoom.jsx'), 'ScrimDraftRoomPage') },
          { path: 'draft-demo', lazy: page(() => import('./pages/events/DraftDemo.jsx'), 'DraftDemoPage') },
          { path: 'practice', lazy: page(() => import('./pages/practice/PracticePages.jsx'), 'PracticePage') },
          { path: 'subs', lazy: page(() => import('./pages/subs/SubsPages.jsx'), 'SubsPage') },
          { path: 'subs/:id', lazy: page(() => import('./pages/subs/SubsPages.jsx'), 'SubRequestPage') },
          { path: 'practice/:code', lazy: page(() => import('./pages/practice/PracticePages.jsx'), 'PracticeRoomPage') },
          { path: 'practice/:code/draft/:game', lazy: page(() => import('./pages/events/DraftRoom.jsx'), 'PracticeDraftRoomPage') },
          { path: 'faq', lazy: page(() => import('./pages/FaqPage.jsx'), 'FaqPage') },
          { path: 'terms', lazy: page(legal, 'TermsPage') },
          { path: 'privacy', lazy: page(legal, 'PrivacyPage') },
          { path: 'support', lazy: page(legal, 'SupportPage') },
          { path: 'support/new', lazy: page(support, 'NewRequestPage') },
          { path: 'support/requests', lazy: page(support, 'MyRequestsPage') },
          { path: 'support/requests/:id', lazy: page(support, 'RequestPage') },
          { path: 'highlights', lazy: page(highlights, 'HighlightsPage') },
          { path: 'highlights/:id', lazy: page(highlights, 'HighlightPage') },
          { path: 'p/:handle', lazy: page(players, 'PlayerPage') },
          { path: 'teams', lazy: page(teams, 'TeamsPage') },
          { path: 'teams/new', lazy: page(teams, 'CreateTeamPage') },
          { path: 't/:slug', lazy: page(teamPage, 'TeamPage') },
          { path: 't/:slug/manage', lazy: page(manage, 'ManageTeamPage') },
          { path: 't/:slug/manage/:tab', lazy: page(manage, 'ManageTeamPage') },
          { path: 'join/:code', lazy: page(() => import('./pages/teams/JoinPage.jsx'), 'JoinPage') },
          { path: 'events', lazy: page(() => import('./pages/events/EventsPage.jsx'), 'EventsPage') },
          { path: 'calendar', lazy: page(() => import('./pages/CalendarPage.jsx'), 'CalendarPage') },
          { path: 'stats', lazy: page(() => import('./pages/StatsPage.jsx'), 'StatsPage') },
          { path: 'stats/players', lazy: page(playerStats, 'PlayerStatsSearchPage') },
          { path: 'stats/live', lazy: page(() => import('./pages/stats/LiveGame.jsx'), 'LiveGamePage') },
          { path: 'stats/players/:uuid', lazy: page(playerStats, 'PlayerStatsPage') },
          { path: 'stats/matches/:id', lazy: page(playerStats, 'MatchStatsPage') },
          { path: 'stats/matches/:id/analysis', lazy: page(() => import('./pages/stats/MatchAnalysis.jsx'), 'MatchAnalysisPage') },
          { path: 'history', lazy: page(() => import('./pages/HistoryPages.jsx'), 'HistoryPage') },
          { path: 'history/:slug', lazy: page(() => import('./pages/HistoryPages.jsx'), 'HistoryEventPage') },
          { path: 'compare', lazy: page(() => import('./pages/ComparePage.jsx'), 'ComparePage') },
          { path: 'leaderboard', lazy: page(() => import('./pages/LeaderboardPage.jsx'), 'LeaderboardPage') },
          { path: 'e/:slug', lazy: page(eventPage, 'EventPage') },
          { path: 'e/:slug/register', lazy: page(() => import('./pages/events/RegisterPage.jsx'), 'RegisterPage') },
          { path: 'e/:slug/draft-day', lazy: page(() => import('./pages/events/PlayerDraft.jsx'), 'DraftDayPage') },
          { path: 'e/:slug/manage', lazy: page(eventDashboard, 'ManageEventPage') },
          { path: 'e/:slug/manage/:tab', lazy: page(eventDashboard, 'ManageEventPage') },
          { path: 'e/:slug/matches/:id', lazy: page(() => import('./pages/events/MatchPage.jsx'), 'MatchPage') },
          { path: 'e/:slug/matches/:id/draft/:game', lazy: page(() => import('./pages/events/DraftRoom.jsx'), 'DraftRoomPage') },
          { path: 'e/:slug/:tab', lazy: page(eventPage, 'EventPage') },
          { path: 'organizers', lazy: page(orgs, 'OrganizersPage') },
          { path: 'organizers/new', lazy: page(orgs, 'CreateOrgPage') },
          { path: 'o/:slug', lazy: page(orgs, 'OrgPage') },
          { path: 'o/:slug/events/new', lazy: page(() => import('./pages/events/CreateEventPage.jsx'), 'CreateEventPage') },
          { path: 'o/:slug/manage', lazy: page(orgs, 'ManageOrgPage') },
          { path: 'o/:slug/manage/:tab', lazy: page(orgs, 'ManageOrgPage') },
          { path: 'admin', lazy: page(() => import('./pages/admin/AdminPage.jsx'), 'AdminPage') },
          { path: 'admin/:tab', lazy: page(() => import('./pages/admin/AdminPage.jsx'), 'AdminPage') },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <SessionProvider>
      <ToastProvider>
        <RouterProvider router={router} />
      </ToastProvider>
    </SessionProvider>
  </StrictMode>,
)
